// Free-tools registry. One entry per tool page under /tools/<slug>/.
// `live: false` entries are hidden from the index and sitemap until their wave ships
// (see docs/free-tools-research.md for the research behind this list).
//
// COMPLIANCE GUARDRAILS (same as lib/site.ts — YMYL safety content):
//  - Tools inform and draft; they never certify or guarantee compliance.
//  - Every output carries the competent-person disclaimer.

export type ToolCategory = "calculator" | "checker" | "assessment" | "document" | "permit";

export const TOOL_CATEGORIES: Record<ToolCategory, { label: string; blurb: string }> = {
  calculator: { label: "Calculators", blurb: "Quick numeric checks against HSE thresholds." },
  checker: { label: "Checkers & quizzes", blurb: "Find out where you stand before someone else checks for you." },
  assessment: { label: "Assessment builders", blurb: "Structured, printable assessments for specific risks." },
  document: { label: "Document builders", blurb: "Build a complete, printable site document in minutes." },
  permit: { label: "Permits to work", blurb: "Issue and record high-risk work permits." },
};

export interface Tool {
  slug: string;
  name: string;
  /** Card + meta description. */
  description: string;
  category: ToolCategory;
  /** Hidden from index + sitemap until the wave ships. */
  live: boolean;
  /** Eyebrow keyword line on the tool page hero. */
  eyebrow?: string;
  /**
   * Hero visual overrides. By default the hero graphic + badge are keyed off
   * `category`, which is wrong for tools that don't fit their category's
   * default story (e.g. the CIS calculator is not "checked against HSE
   * thresholds"). `heroVisual` swaps the graphic; `heroBadge`/`heroBadgeSub`
   * replace the badge line and its right-hand sub-label.
   */
  heroVisual?: ToolCategory | "finder";
  heroBadge?: string;
  heroBadgeSub?: string;
}

export const TOOLS: Tool[] = [
  {
    slug: "stress-risk-assessment",
    name: "Stress risk assessment",
    description:
      "Assess work-related stress against the six HSE Management Standards — demands, control, support, relationships, role and change — flag what needs action and print a record. Built around the HSE framework and the employer's duty to assess stress.",
    category: "assessment",
    live: true,
    eyebrow: "Work-related stress",
  },
  {
    slug: "legionella-risk-assessment",
    name: "Legionella risk assessment",
    description:
      "Work through the legionella risk factors and L8 controls for a hot & cold water system — temperatures, flushing little-used outlets, descaling showers and the written scheme — and print a record. Built around HSE ACoP L8 and HSG274; flags cooling towers, spas and healthcare.",
    category: "assessment",
    live: true,
    eyebrow: "Legionella / water safety",
  },
  {
    slug: "health-safety-policy",
    name: "Health & safety policy generator",
    description:
      "Build the three-part health & safety policy the HSE expects — Statement of Intent, Organisation and Arrangements — from your company details and the topics that apply, then print it. A written policy is a legal duty at 5+ employees.",
    category: "document",
    live: true,
    eyebrow: "Health & safety policy",
    heroVisual: "document",
  },
  {
    slug: "peep-builder",
    name: "PEEP builder",
    description:
      "Build a Personal Emergency Evacuation Plan for anyone who can't evacuate unaided — capture their needs, alerting method and escape strategy (buddy, evacuation chair or refuge) and print the plan. Built around the Regulatory Reform (Fire Safety) Order 2005.",
    category: "document",
    live: true,
    eyebrow: "Personal emergency evacuation plan",
    heroVisual: "document",
  },
  {
    slug: "fire-risk-assessment",
    name: "Fire risk assessment",
    description:
      "Work through the five-step fire risk assessment for non-domestic premises — fire hazards, people at risk and existing precautions, with critical gaps flagged — and print an FRA record. Built around the Regulatory Reform (Fire Safety) Order 2005; flags when a competent assessor is needed.",
    category: "assessment",
    live: true,
    eyebrow: "Fire risk assessment",
  },
  {
    slug: "fire-door-inspection-checklist",
    name: "Fire door inspection checklist",
    description:
      "Work through certification, gaps, intumescent and smoke seals, hinges, the self-closer, glazing and operation — critical defects flagged — and print an inspection record. Built around BS 8214 and the Fire Safety (England) Regulations 2022.",
    category: "assessment",
    live: true,
    eyebrow: "Fire door inspection",
  },
  // ── Calculators ─────────────────────────────────────────────────────────
  {
    slug: "havs-calculator",
    name: "HAVS calculator",
    description:
      "Add each vibrating tool and trigger time to get daily exposure points and A(8), checked against the HSE action and limit values.",
    category: "calculator",
    live: true,
    eyebrow: "Hand-arm vibration",
  },
  {
    slug: "risk-matrix-calculator",
    name: "Risk matrix calculator",
    description:
      "Score likelihood × severity on the standard 5×5 matrix, get the risk rating and what it means for your RAMS.",
    category: "calculator",
    live: true,
    eyebrow: "5×5 risk scoring",
    // It's a scoring tool, not a threshold check — use the matrix visual.
    heroVisual: "assessment",
  },
  {
    slug: "noise-exposure-calculator",
    name: "Noise exposure calculator",
    description:
      "Estimate daily noise exposure (LEP,d) from task noise levels and durations, checked against the action values.",
    category: "calculator",
    live: true,
    eyebrow: "Noise at work",
  },
  {
    slug: "rams-cost-calculator",
    name: "RAMS cost calculator",
    description:
      "What RAMS paperwork really costs you per year — your time, consultant fees and the price of a rejected document.",
    category: "calculator",
    live: true,
    eyebrow: "Time & cost",
    // Cost tool, not an HSE-threshold check.
    heroBadge: "Your annual RAMS bill, itemised",
    heroBadgeSub: "time + fees + rework",
  },
  {
    slug: "cis-tax-calculator",
    name: "CIS tax calculator",
    description:
      "Work out CIS deductions at 0%, 20% or 30%, with materials and VAT handled correctly.",
    category: "calculator",
    live: true,
    eyebrow: "Construction Industry Scheme",
    // Tax tool — HMRC rates, not HSE thresholds.
    heroBadge: "Checked against HMRC CIS rates",
    heroBadgeSub: "0% / 20% / 30%",
  },
  {
    slug: "first-aid-needs-calculator",
    name: "First aid needs calculator",
    description:
      "First aiders, appointed persons and kits your site needs — worked out from headcount, risk level and site spread, per HSE guidance.",
    category: "calculator",
    live: true,
    eyebrow: "First aid at work",
  },
  {
    slug: "welfare-facilities-calculator",
    name: "Welfare facilities calculator",
    description:
      "Toilets, washbasins, rest space and drying room provision for your site headcount — checked against CDM 2015 Schedule 2.",
    category: "calculator",
    live: true,
    eyebrow: "CDM Schedule 2",
    heroBadge: "Checked against CDM Schedule 2",
  },
  {
    slug: "fire-extinguisher-calculator",
    name: "Fire extinguisher calculator",
    description:
      "How many extinguishers a floor needs and which types — worked out from floor area and the fire risks present, per BS 5306-8.",
    category: "calculator",
    live: true,
    eyebrow: "Fire cover",
    heroBadge: "Checked against BS 5306-8",
  },
  {
    slug: "sling-angle-calculator",
    name: "Sling angle calculator",
    description:
      "Tension per sling leg from load, leg count and included angle — with mode factors and warnings as the angle widens.",
    category: "calculator",
    live: true,
    eyebrow: "Lifting accessories",
    heroBadge: "Mode-factor maths, live",
    heroBadgeSub: "watch the angle",
  },

  // ── Checkers ────────────────────────────────────────────────────────────
  {
    slug: "cdm-duty-checker",
    name: "CDM duty checker",
    description:
      "Answer a few questions about the project and see your CDM 2015 duties — and whether it's F10 notifiable.",
    category: "checker",
    live: true,
    eyebrow: "CDM 2015",
  },
  {
    slug: "rams-checker",
    name: "RAMS pre-submission checker",
    description:
      "Run your RAMS against the checks principal contractors commonly make before review and sign-off.",
    category: "checker",
    live: true,
    eyebrow: "Pre-submission review",
  },
  {
    slug: "rams-rejection-checker",
    name: "RAMS rejection checker",
    description:
      "Paste your RAMS and get it reviewed the way a principal contractor would — eight checks, severity-ranked findings and concrete fixes.",
    category: "checker",
    live: true,
    eyebrow: "AI document review",
    heroBadge: "Reviewed like a PC would",
    heroBadgeSub: "takes about a minute",
  },
  {
    slug: "ssip-readiness-checker",
    name: "SSIP readiness checker",
    description:
      "Check your policies, RAMS, insurance and records against what CHAS-style SSIP assessments ask for.",
    category: "checker",
    live: true,
    eyebrow: "CHAS / SSIP",
  },
  {
    slug: "hse-inspection-checker",
    name: "HSE inspection readiness checker",
    description:
      "Would your site stand up to an unannounced HSE inspection? Check against what inspectors look for.",
    category: "checker",
    live: true,
    eyebrow: "Inspection readiness",
  },
  {
    slug: "ppe-selector",
    name: "PPE selector",
    description:
      "Pick your tasks and hazards, get the PPE to specify in your RAMS with the relevant EN standards.",
    category: "checker",
    live: true,
    eyebrow: "PPE & EN standards",
  },
  {
    slug: "cscs-card-finder",
    name: "CSCS card finder",
    description:
      "Find which CSCS card fits your role, qualifications and experience — and what you need to get it.",
    category: "checker",
    live: true,
    eyebrow: "CSCS cards",
  },
  {
    slug: "riddor-checker",
    name: "RIDDOR checker",
    description:
      "Work through whether an incident is RIDDOR reportable, what to report and the deadline.",
    category: "checker",
    live: true,
    eyebrow: "Is it reportable?",
  },
  {
    slug: "excavation-support-selector",
    name: "Excavation support selector",
    description:
      "Battering, benching, trench boxes or sheeting — what your excavation needs based on depth, soil and what's nearby.",
    category: "checker",
    live: true,
    eyebrow: "Excavation safety",
  },
  {
    slug: "asbestos-survey-checker",
    name: "Asbestos survey checker",
    description:
      "Does your job need a management survey or a refurbishment & demolition survey? Answer a few questions about the building and the work.",
    category: "checker",
    live: true,
    eyebrow: "Which survey?",
  },
  {
    slug: "wind-limits-checker",
    name: "Wind limits checker",
    description:
      "Stop-work wind speeds for MEWPs, cranes, roof work, hoarding and lifting — with Beaufort cues you can judge on site.",
    category: "checker",
    live: true,
    eyebrow: "Weather limits",
  },

  // ── Assessment builders ─────────────────────────────────────────────────
  {
    slug: "coshh-assessment",
    name: "COSHH assessment builder",
    description:
      "Build a substance-by-substance COSHH assessment — hazards, exposure routes, controls, PPE/RPE and review dates.",
    category: "assessment",
    live: true,
    eyebrow: "Hazardous substances",
  },
  {
    slug: "manual-handling-assessment",
    name: "Manual handling assessment",
    description:
      "TILE-style task assessment — task, individual, load, environment — with a printable record.",
    category: "assessment",
    live: true,
    eyebrow: "TILE assessment",
  },
  {
    slug: "working-at-height-assessment",
    name: "Work at height assessment",
    description:
      "Ladders, towers, MEWPs and fragile roofs — assess the job and capture the rescue plan, not just the access kit.",
    category: "assessment",
    live: true,
    eyebrow: "Work at height",
  },
  {
    slug: "confined-space-assessment",
    name: "Confined space assessment",
    description:
      "Atmosphere, access and egress, rescue arrangements and whether the job needs a permit.",
    category: "assessment",
    live: true,
    eyebrow: "Confined spaces",
  },
  {
    slug: "powra",
    name: "Point of work risk assessment",
    description:
      "The quick daily check before work starts — conditions, changes, services, and a stop/go decision.",
    category: "assessment",
    live: true,
    eyebrow: "POWRA / dynamic RA",
  },
  {
    slug: "nearest-ae-finder",
    name: "Nearest A&E finder",
    description:
      "Enter the site postcode and get the emergency information block for your RAMS — nearest A&E, directions and what to record.",
    category: "assessment",
    live: true,
    eyebrow: "Emergency arrangements",
    // A lookup, not a scored assessment — gets its own finder visual.
    heroVisual: "finder",
    heroBadge: "Postcode → nearest A&E",
    heroBadgeSub: "ranked by distance",
  },

  // ── Document builders ───────────────────────────────────────────────────
  {
    slug: "construction-phase-plan",
    name: "Construction phase plan builder",
    description:
      "Build a proportionate CPP for small and domestic projects — the document CDM 2015 requires before work starts.",
    category: "document",
    live: true,
    eyebrow: "CDM 2015",
  },
  {
    slug: "site-induction",
    name: "Site induction builder",
    description:
      "Site rules, welfare, emergency arrangements, access and key hazards — with a signature sheet.",
    category: "document",
    live: true,
    eyebrow: "Inductions",
  },
  {
    slug: "fire-plan",
    name: "Site fire plan builder",
    description:
      "Ignition sources, hot works, escape routes, detection and extinguishers for your site fire plan.",
    category: "document",
    live: true,
    eyebrow: "Fire safety",
  },
  {
    slug: "site-safety-plan-sketcher",
    name: "Site safety plan sketcher",
    description:
      "Upload a plan or photo and mark up routes, exclusion zones, services and permit areas for a RAMS-ready briefing sketch.",
    category: "document",
    live: true,
    eyebrow: "Site plans & sketches",
    heroBadge: "Plan + sketch + layer check",
    heroBadgeSub: "RAMS-ready",
  },
  {
    slug: "traffic-management-plan",
    name: "Traffic management plan builder",
    description:
      "Pedestrian segregation, reversing, delivery routes and exclusion zones in a printable plan.",
    category: "document",
    live: true,
    eyebrow: "Site traffic",
  },
  {
    slug: "lift-plan",
    name: "Lift plan builder",
    description:
      "Load, equipment, exclusion zones and the appointed person / signaller roles for a basic lift plan.",
    category: "document",
    live: true,
    eyebrow: "LOLER lifting",
  },
  {
    slug: "incident-report",
    name: "Incident & near-miss report builder",
    description:
      "Capture the facts, root cause and corrective actions — with a built-in RIDDOR reportability prompt.",
    category: "document",
    live: true,
    eyebrow: "Incidents",
  },
  {
    slug: "safety-alert",
    name: "Safety alert builder",
    description:
      "Turn an incident or near miss into a one-page briefing bulletin your crews will actually read.",
    category: "document",
    live: true,
    eyebrow: "Briefings",
  },
  {
    slug: "toolbox-talk",
    name: "Toolbox talk builder",
    description:
      "Build a short, structured toolbox talk with an attendance and sign-off sheet.",
    category: "document",
    live: true,
    eyebrow: "Toolbox talks",
  },
  {
    slug: "f10-notification-drafter",
    name: "F10 notification drafter",
    description:
      "Assemble every field the HSE F10 form asks for — ready to paste into the official notification, with the notifiability test built in.",
    category: "document",
    live: true,
    eyebrow: "CDM 2015",
  },
  {
    slug: "training-matrix",
    name: "Training matrix builder",
    description:
      "Workers × tickets and training with expiry dates — the matrix SSIP assessors ask for, with expiry warnings and calendar reminders.",
    category: "document",
    live: true,
    eyebrow: "Competence records",
  },

  // ── Permits to work ─────────────────────────────────────────────────────
  {
    slug: "hot-works-permit",
    name: "Hot works permit",
    description:
      "Issue a hot works permit — fire watch, extinguishers, isolation of detectors and the close-out check.",
    category: "permit",
    live: true,
    eyebrow: "Hot works",
  },
  {
    slug: "permit-to-dig",
    name: "Permit to dig",
    description:
      "Buried services checks, plans, CAT & genny scan and safe digging conditions before excavation starts.",
    category: "permit",
    live: true,
    eyebrow: "Excavation",
  },
  {
    slug: "confined-space-permit",
    name: "Confined space entry permit",
    description:
      "Entry controls, atmosphere testing, rescue arrangements and time limits for confined space work.",
    category: "permit",
    live: true,
    eyebrow: "Confined spaces",
  },
  {
    slug: "isolation-permit",
    name: "Isolation / lock-off permit",
    description:
      "Record electrical and mechanical isolations, lock-off points, proving dead and re-energisation.",
    category: "permit",
    live: true,
    eyebrow: "Isolation / lock-off",
  },
  {
    slug: "roof-access-permit",
    name: "Roof access permit",
    description:
      "Fragile surfaces, edge protection, access route and weather limits before anyone goes on the roof.",
    category: "permit",
    live: true,
    eyebrow: "Roof access",
  },
  {
    slug: "lifting-permit",
    name: "Lifting operation permit",
    description:
      "Authorise a lifting operation — equipment checks, exclusion zone, appointed person and signaller.",
    category: "permit",
    live: true,
    eyebrow: "Lifting operation",
  },
];

export const LIVE_TOOLS = TOOLS.filter((t) => t.live);

export function getTool(slug: string): Tool | undefined {
  return TOOLS.find((t) => t.slug === slug);
}

// Ring over the live tools (grouped by category so neighbours stay relevant),
// starting just after the current tool. A rotating window — rather than always
// returning the same first matches — guarantees every tool is surfaced by `count`
// others, so none is left with a single internal inlink (helps crawl/indexing).
export function relatedTools(slug: string, count = 3): Tool[] {
  const ring = [...LIVE_TOOLS].sort((a, b) =>
    a.category === b.category ? a.name.localeCompare(b.name) : a.category.localeCompare(b.category),
  );
  const idx = ring.findIndex((t) => t.slug === slug);
  if (idx === -1) return ring.slice(0, count);
  const out: Tool[] = [];
  for (let i = 1; i <= count && i < ring.length; i++) out.push(ring[(idx + i) % ring.length]);
  return out;
}
